import { useState, useEffect, useRef } from "react";
import { Conversation } from "@/types/chat";
import { Trash2 } from "lucide-react";

interface SidebarProps {
    conversations: Conversation[];
    onDeleteConversation: (id: string) => void;
    onNewConversation: () => void;
    onSelectConversation: (id: string) => void;
    selectedConversationId?: string;
    isOpen?: boolean;
    isLargeScreen?: boolean;
    onClose?: () => void;
}

const SWIPE_THRESHOLD = 60;
const AUTO_CLOSE_DELETE_MS = 2500;

export default function Sidebar({
    conversations,
    onNewConversation,
    onSelectConversation,
    onDeleteConversation,
    selectedConversationId,
    isOpen = false,
    isLargeScreen = false,
    onClose,
}: SidebarProps) {
    const [mounted, setMounted] = useState(false);
    const [openDeleteId, setOpenDeleteId] = useState<string | null>(null);

    const deleteTimerRef = useRef<number | null>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        return () => {
            if (deleteTimerRef.current) {
                window.clearTimeout(deleteTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (openDeleteId && !isLargeScreen) {
            if (deleteTimerRef.current) {
                window.clearTimeout(deleteTimerRef.current);
            }

            deleteTimerRef.current = window.setTimeout(() => {
                setOpenDeleteId(null);
            }, AUTO_CLOSE_DELETE_MS);
        }
    }, [openDeleteId, isLargeScreen]);

    const clearDeleteState = () => {
        setOpenDeleteId(null);
        if (deleteTimerRef.current) {
            window.clearTimeout(deleteTimerRef.current);
            deleteTimerRef.current = null;
        }
    };

    const armDelete = (id: string) => {
        setOpenDeleteId((current) => (current === id ? null : id));
    };

    const confirmDelete = (id: string) => {
        clearDeleteState();
        onDeleteConversation(id);
    };

    const handleDeletePress = (id: string) => {
        if (openDeleteId === id) {
            confirmDelete(id);
            return;
        }

        armDelete(id);
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        touchStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
        };
    };

    const handleTouchEnd = (id: string, e: React.TouchEvent<HTMLDivElement>) => {
        const start = touchStartRef.current;
        const touch = e.changedTouches[0];

        if (!start || !touch) {
            touchStartRef.current = null;
            return;
        }

        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;

        touchStartRef.current = null;

        // Swipe right only: reveal delete action from the left
        if (dx > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
            setOpenDeleteId(id);

            if (deleteTimerRef.current) {
                window.clearTimeout(deleteTimerRef.current);
            }

            deleteTimerRef.current = window.setTimeout(() => {
                setOpenDeleteId(null);
            }, AUTO_CLOSE_DELETE_MS);
            return;
        }

        // Swipe left or small move: close any open delete state
        if (dx < -30 || Math.abs(dx) <= SWIPE_THRESHOLD) {
            setOpenDeleteId((current) => (current === id ? null : current));
        }
    };

    const handleSelect = (id: string) => {
        clearDeleteState();
        onSelectConversation(id);
    };

    const handleNewConversation = () => {
        clearDeleteState();
        onNewConversation();
    };

    // Determine sidebar visibility
    const shouldShow = isLargeScreen || isOpen;

    // Base classes for the sidebar
    const baseClasses =
        "flex flex-col w-80 max-w-[80%] bg-gray-200 xl:rounded-2xl transition-all duration-300 ease-in-out h-[50vh]";

    // Responsive classes
    const responsiveClasses = isLargeScreen
        ? "block relative"
        : mounted && shouldShow
          ? "fixed left-0 top-0 h-full z-50 transform translate-x-0"
          : "fixed left-0 top-0 h-full z-50 transform -translate-x-full";

    // Mobile overlay
    const overlayClasses = isLargeScreen
        ? "hidden"
        : mounted && shouldShow
          ? "fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          : "fixed inset-0 bg-black/50 z-40 opacity-0 pointer-events-none transition-opacity duration-300";

    return (
        <>
            {/* Mobile Overlay */}
            {!isLargeScreen && mounted && (
                <div className={overlayClasses} onClick={onClose} />
            )}

            {/* Sidebar */}
            <div className={`${baseClasses} ${responsiveClasses}`}>
                {/* New Conversation Button */}
                <div className="p-4 border-b border-gray-200">
                    <button
                        onClick={handleNewConversation}
                        className="w-full bg-slate-700 hover:bg-slate-800 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 cursor-pointer"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                        </svg>
                        <span>Νέο Session</span>
                    </button>
                </div>

                {/* History Section */}
                <div className="flex-1 overflow-y-auto absolute bottom-0 w-full">
                    <div className="p-4 ">
                        <div className="flex items-center space-x-2 mb-4">
                            <svg
                                className="w-5 h-5 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <h3 className="text-sm font-medium text-gray-700">
                                Ιστορικό
                            </h3>
                        </div>

                        <div className="space-y-2 overflow-y-auto h-[20vh]">
                            {conversations.map((conversation) => {
                                const isOpenDelete = openDeleteId === conversation.id;

                                return (
                                    <div
                                        key={conversation.id}
                                        className="relative overflow-hidden rounded-lg"
                                    >
                                        {/* Left red delete action */}
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeletePress(conversation.id);
                                            }}
                                            className={`absolute inset-y-0 left-0 z-10 flex w-16 items-center justify-center bg-red-600 text-white transition-transform duration-300 ease-out ${
                                                isOpenDelete
                                                    ? "translate-x-0"
                                                    : "-translate-x-full"
                                            }`}
                                            aria-label="Delete conversation"
                                            title={
                                                isOpenDelete
                                                    ? "Πάτησε ξανά για διαγραφή"
                                                    : "Διαγραφή session"
                                            }
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>

                                        {/* Main item */}
                                        <div
                                            onClick={() => handleSelect(conversation.id)}
                                            onTouchStart={handleTouchStart}
                                            onTouchEnd={(e) => handleTouchEnd(conversation.id, e)}
                                            className={`flex items-center justify-between border p-3 transition-all duration-300 ease-out touch-pan-y select-none ${
                                                selectedConversationId === conversation.id
                                                    ? "bg-slate-500 text-white border-slate-400"
                                                    : "bg-white hover:bg-slate-100 border-transparent"
                                            } ${
                                                isOpenDelete ? "translate-x-16" : "translate-x-0"
                                            }`}
                                        >
                                            <div className="min-w-0 flex-1 text-left">
                                                <div className="text-sm font-semibold truncate">
                                                    {conversation.title}
                                                </div>
                                                <div className="text-xs mt-1">
                                                    {conversation.date}
                                                </div>
                                            </div>

                                            {/* Right trash trigger */}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeletePress(conversation.id);
                                                }}
                                                className={`ml-2 rounded-full p-2 transition-colors ${
                                                    isOpenDelete
                                                        ? "bg-red-600 text-white"
                                                        : "text-gray-400 hover:bg-red-50 hover:text-red-500"
                                                }`}
                                                aria-label="Delete conversation"
                                                title={
                                                    isOpenDelete
                                                        ? "Πάτησε ξανά για διαγραφή"
                                                        : "Διαγραφή session"
                                                }
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}